import { useEffect, useState } from 'react';
import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { intervalToDuration } from 'date-fns';
import { GetStaticPaths, GetStaticProps } from 'next';
import SbEditable from 'storyblok-react';

import { Block, Seo } from '~/components';

import {
  RegistrationCard,
  RelatedEvents,
  WebinarDescription,
  WebinarHeader,
} from '~/components/Webinars';
import {
  addFormFieldsToStory,
  enhancedStaticProps,
  getSlugWithLocale,
  sdk as sdkClient,
  withLayout,
} from '~/lib';

export interface WebinarProps extends WithStoryProps {}

const Webinar = ({ story }: WebinarProps) => {
  const content = story?.content;
  const date =
    new Date(`${content?.date?.replace(/-/g, '/')} GMT+1:00`) || new Date();

  const [countdown, setCountdown] = useState(
    intervalToDuration({ start: new Date(), end: date }),
  );

  useEffect(() => {
    const updateCountdown = setInterval(() => {
      setCountdown(
        intervalToDuration({
          start: new Date(),
          end: date,
        }),
      );
    }, 1000);

    return () => clearInterval(updateCountdown);
  }, []);

  return (
    <div>
      <Seo story={story} />

      <SbEditable content={content}>
        <WebinarHeader content={content} />

        <RegistrationCard content={content} countdown={countdown} />

        <WebinarDescription content={content} />

        {content?.related_events?.length > 0 && (
          <RelatedEvents
            title={content?.related_title}
            events={content?.related_events}
          />
        )}
      </SbEditable>

      {!!content?.blocks?.[0]?.component &&
        content?.blocks?.map((block) => {
          return (
            <Block
              key={block?._uid}
              component={story?.content?.component}
              sectionType={block?.component}
              content={block}
              title={story?.content?.seo?.title || story?.content?.title}
            />
          );
        })}
    </div>
  );
};

export default withLayout(withStory(Webinar));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ params: { slug }, sdk, locale, defaultLocale }) => {
    let story;
    let authors;
    let notFound = false;

    try {
      story = (
        await sdk.webinarItem({
          slug: getSlugWithLocale(`webinars/${slug}`, locale, defaultLocale),
          lang: locale,
        })
      ).WebinarItem;
      story = await addFormFieldsToStory(story);
    } catch (e) {
      notFound = true;
    }

    try {
      authors = (await sdkClient.authorCardItems()).AuthorcardItems.items;
    } catch (e) {
      console.log(e);
    }

    return {
      props: {
        story: {
          ...story,
          content: {
            ...story?.content,
            related_events:
              story?.content?.related_events?.length > 0
                ? story?.content?.related_events?.map((w) => ({
                    ...w,
                    content: {
                      ...w?.content,
                      hosts: w?.content?.hosts?.map((h) =>
                        authors?.find((a) => a?.uuid === h),
                      ),
                    },
                  }))
                : story?.content?.related_events,
          },
        },
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);

export const getStaticPaths: GetStaticPaths = async () => {
  let stories;

  try {
    stories = (await sdkClient.webinarItems()).WebinarItems.items;
  } catch (e) {
    console.log(e);
  }

  return {
    paths: stories?.map(({ slug }) => {
      return {
        params: { slug },
      };
    }),
    fallback: 'blocking',
  };
};
